import { h } from "../ui/dom";
import { km, type LatLng } from "../game/geo";
import { distanceScore, hintCap, per1000, PIN_MAX, pinPoints } from "../game/scoring";
import { journeyOrder } from "../game/route";
import type { Credits } from "../game/knowledge";
import type { Overlay } from "../map/MapView";
import type { Place } from "../puzzles/types";
import { t } from "../strings";
import type { Playable } from "../game/derive";
import { tapToExplain, type Host, type RoundModule, type RoundState } from "./types";
import { COUNT_MS, offText, revealLayers } from "./reveal";
import { routeLayers, routeRows } from "./trail";
import { clearLive, liveKey, loadLive, parsePin, saveLive } from "./live";

type P = Extract<Playable, { type: "pin" }>;
interface S extends RoundState {
  /** The one pin: a tap elsewhere moves it, there is never a second. */
  guess: LatLng | null;
  dist: number;
  /** Reveal progress after Lock in (0 → 1): the distance line grows and counts up, then the rings. */
  t: number;
  /** Then the person's trail through the true spot (0 → 1), drawn stop by stop. */
  u: number;
  /** Hints shown (0–2); each lowers the most the round can score (scoring.ts HINT_CAPS). Survives a reload. */
  hints: number;
  /** Storage key of this round's live state. */
  key: string;
}

/** The trail is drawn one stop at a time after the count-up. */
const TRAIL_MS = 900;
/** Pin ids of the trail stops (tap for the note). */
const TRAIL = 300;

/** The answer and its two trail stops in time order, and where the answer sits among them; null without a trail. */
function trailOf(p: P): { stops: Place[]; at: number } | null {
  if (!p.trail) return null;
  const { stops } = journeyOrder([p.answer, ...p.trail]);
  return { stops, at: stops.indexOf(p.answer) };
}

const credits = (p: P, known: boolean): Credits => {
  const c: Credits = { a: known ? "k" : "l" };
  p.trail?.forEach((_, i) => (c[`t${i}`] = "l"));
  return c;
};

function overlay(host: Host, s: S, p: P): Overlay {
  const C = host.map.colors;
  if (!s.done || !s.guess) return { pins: s.guess ? [{ ...s.guess, kind: "guess", color: C.guess, z: 2, tag: "guess" }] : [], cities: p.cities };
  const tr = s.u > 0 ? trailOf(p) : null;
  const answer = { ...p.answer, color: C.accent, halo: true, label: p.answer.name, sub: tr ? (p.answer.when ?? null) : null, id: TRAIL + (tr?.at ?? 0), text: tr ? String(tr.at + 1) : null };
  const rv = revealLayers(C, { guess: s.guess, answer, km: s.dist, t: s.t });
  if (!tr) return { ...rv, cities: true };
  // The person's three steps through the true spot, in time order (the answer marker is the middle of the story).
  const route = routeLayers({ stops: tr.stops, color: C.accent, idBase: TRAIL, upTo: Math.ceil(s.u * tr.stops.length), skip: [tr.at] });
  return { pins: [...route.pins, ...rv.pins], lines: [...route.lines, ...rv.lines], rings: rv.rings, cities: true };
}

export const pin: RoundModule<P, S> = {
  init: (p, host) => {
    const key = liveKey(host.level, p.id);
    const live = loadLive(key, parsePin);
    return { done: false, guess: null, dist: 0, t: 1, u: 1, hints: Math.min(live?.hints ?? 0, p.hints.length), key };
  },

  view(host, s, p, ms) {
    if (s.done && s.guess) host.map.fitPoints([s.guess, p.answer, ...(p.trail ?? [])], { ms, maxWorldPx: 60000 });
    else if (p.region)
      host.map.fitPoints(
        p.region.map(([lat, lng]) => ({ lat, lng })),
        { ms },
      );
    else host.map.worldView(ms);
  },

  render(host, s, p) {
    host.map.set(overlay(host, s, p));
    host.map.onPinClick = null;
    const tr = s.done ? trailOf(p) : null;
    if (tr) tapToExplain(host, Object.fromEntries(tr.stops.map((x, i) => [TRAIL + i, x])));
    host.map.onMapClick = s.done
      ? null
      : (ll) => {
          const first = !s.guess;
          s.guess = ll;
          host.draw();
          if (first) host.firstPin?.(ll);
        };
    host.map.setPicking(!s.done);
    host.frame(
      p,
      // Beside the title: hints used and the most this round can still score.
      p.hints.length ? h("div", { class: "kicker", "data-act": "pin-worth" }, h("span", null, t.pin.hintsUsed(s.hints, p.hints.length)), h("span", null, t.pin.worthUpTo(hintCap(s.hints)))) : null,
      h("p", { class: "clue" }, p.clue),
      s.hints ? h("ol", { class: "hints", "aria-label": t.pin.hintsList }, p.hints.slice(0, s.hints).map((x, i) => h("li", null, h("b", null, `${t.pin.hintLabel(i + 1)}: `), x))) : null,
      // No "tap the map" line before the pin (Egor, 30 Sep); once it is down, say it can be moved.
      !s.done && s.guess ? h("p", { class: "clue how" }, t.pin.moveHint) : null,
      s.done
        ? [tr ? routeRows(tr.stops, { label: t.pin.trail, mark: tr.at, markText: t.pin.here, kind: "pin" }) : null, host.verdict()]
        : h("div", { class: "row" }, lockButton(host, s, p), s.hints < p.hints.length ? hintButton(host, s, p) : null),
    );
  },
};

/** Show the next hint: one step down the ladder (20 → 15 → 10), announced, kept across a reload. */
function hintButton(host: Host, s: S, p: P): HTMLElement {
  return h(
    "button",
    {
      class: "btn ghost",
      type: "button",
      "data-act": "hint",
      "data-key": "hint",
      onclick: () => {
        if (s.done || s.hints >= p.hints.length) return;
        s.hints++;
        saveLive(s.key, { hints: s.hints });
        host.announce(t.pin.hintAnnounce(s.hints, p.hints[s.hints - 1]!, hintCap(s.hints)));
        host.draw();
      },
    },
    t.pin.hintAsk(hintCap(s.hints + 1)),
  );
}

/** Score the one pin: distance points, capped by the hints used; then the reveal and the trail. */
function lockButton(host: Host, s: S, p: P): HTMLElement {
  return h(
    "button",
    {
      class: "btn",
      type: "button",
      "data-act": "submit",
      disabled: !s.guess,
      onclick: () => {
        if (!s.guess) return;
        s.dist = km(s.guess, p.answer);
        s.t = 0;
        s.u = 0;
        clearLive(s.key);
        const score = pinPoints(s.dist, s.hints);
        // "Known" on the Knowledge Map is about where the pin landed, not the hint cap.
        const known = per1000(distanceScore(s.dist), PIN_MAX) >= 600;
        host.finish(score, { km: Math.round(s.dist), at: [Math.round(s.guess.lat * 1e4) / 1e4, Math.round(s.guess.lng * 1e4) / 1e4], ...(p.hints.length ? { hints: s.hints } : {}) }, offText(t.ui.verdict(per1000(score, PIN_MAX)), s.dist), credits(p, known));
        pin.view(host, s, p, 900);
        // the count-up, then the trail stop by stop
        const total = COUNT_MS + (p.trail ? TRAIL_MS : 0);
        host.animate(total, (x) => {
          const ms = x * total;
          s.t = Math.min(1, ms / COUNT_MS);
          s.u = x >= 1 ? 1 : Math.max(0, (ms - COUNT_MS) / TRAIL_MS);
          host.map.set(overlay(host, s, p));
        });
      },
    },
    t.pin.lock,
  );
}
